import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowWaitApiService } from '../services/workflow-wait-api.service';
import { WorkflowApiService } from '../services/workflow-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { WorkflowWaitQueueResponse } from '../models/workflow-wait.model';
import { workflowObjectTypeLabel, workflowQueueTypeLabel, workflowStatusLabel } from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-wait-queue',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './workflow-wait-queue.component.html'
})
export class WorkflowWaitQueueComponent implements OnInit, OnDestroy {
  /**
   * Etiquetas legibles: diccionario único del módulo (`models/workflow-labels.ts`). Se usan como
   * RESPALDO de los campos `*Label` que resuelve el backend — cuando el DTO no los trae, la
   * plantilla pintaba el enum del motor en crudo.
   */
  readonly statusText = workflowStatusLabel;
  readonly queueTypeText = workflowQueueTypeLabel;
  readonly objectTypeText = workflowObjectTypeLabel;

  items: WorkflowWaitQueueResponse[] = [];
  loading = false;
  processing = false;
  error = '';

  filterStatus = 'PENDING';
  filterQueueType = '';

  /**
   * INC_CRM_020 (6) inc3 — «los filtros Workflow ID y Enrollment ID deberían ser por nombre y no
   * por ID». El de workflow pasa a ser un desplegable que MUESTRA nombres (y sigue enviando el id,
   * que es lo que entiende el endpoint); el de inscripción se sustituye por una búsqueda contra el
   * nombre del registro inscrito, que es lo que el usuario reconoce — nadie sabe qué inscripción
   * es la número 10.
   */
  filterWorkflowId: number | '' = '';
  filterRecordSearch = '';

  /** Workflows del negocio para el desplegable, ordenados por nombre. */
  workflowOptions: { id: number; name: string }[] = [];

  /** Si true, "Procesar" también fuerza esperas PENDING aún no vencidas. */
  includeActive = false;

  cancellingId: number | null = null;
  showCancelModal = false;
  cancelItemId: number | null = null;
  cancelReason = '';

  /**
   * INC_CRM_020 (2) inc3 — estos catálogos no coincidían con lo que el motor escribe en
   * `workflow_queue_item`. Ofrecían `COMPLETED`, que el backend NUNCA usa (escribe
   * `PROCESSED`), y omitían `PROCESSED` e `IGNORED`, que son justo los estados de los
   * registros existentes. Con el filtro por defecto en `PENDING` el resultado era una tabla
   * siempre vacía: de ahí el "no se visualizan los registros en espera".
   *
   * Lo mismo con los tipos: `WAIT`, `SCHEDULED` y `BUSINESS_HOURS_WAIT` no existen como
   * `queue_type`; los reales son `DELAY` y `CONDITION_WAIT`.
   */
  readonly statusOptions: { value: string; label: string }[] = [
    { value: '',           label: 'Todos los estados' },
    { value: 'PENDING',    label: 'Pendiente' },
    { value: 'PROCESSING', label: 'Procesando' },
    { value: 'PROCESSED',  label: 'Procesada' },
    { value: 'FAILED',     label: 'Fallida' },
    { value: 'CANCELLED',  label: 'Cancelada' },
    { value: 'IGNORED',    label: 'Ignorada' }
  ];

  readonly queueTypeOptions: { value: string; label: string }[] = [
    { value: '',               label: 'Todos los tipos' },
    { value: 'DELAY',          label: 'Espera por tiempo' },
    { value: 'CONDITION_WAIT', label: 'Espera por condición' }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private waitApi: WorkflowWaitApiService,
    private workflowApi: WorkflowApiService,
    private authService: AuthService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.loadWorkflowOptions();
    this.loadQueue();
  }

  /**
   * INC_CRM_020 (6) inc3 — puebla el desplegable de workflows. Se piden todos de una vez (el
   * listado de un negocio es corto) y se ordenan por nombre: el orden por fecha del endpoint no
   * ayuda a encontrar un workflow concreto en una lista desplegable.
   */
  private loadWorkflowOptions(): void {
    this.workflowApi.getWorkflows({ businessId: this.businessId, page: 0, size: 200 })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          const rows = Array.isArray(res) ? res : res.content;
          this.workflowOptions = rows
            .map(w => ({ id: w.id, name: w.name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'es'));
        },
        // Sin el desplegable la pantalla sigue siendo usable con el resto de filtros:
        // un fallo aquí no debe teñir de rojo la cola.
        error: () => { this.workflowOptions = []; }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getBusinessId() ?? 0;
  }

  loadQueue(): void {
    this.loading = true;
    this.error = '';
    this.waitApi.getQueue({
      businessId: this.businessId,
      ...(this.filterStatus       && { status: this.filterStatus }),
      ...(this.filterQueueType    && { queueType: this.filterQueueType }),
      ...(this.filterWorkflowId !== '' && { workflowId: Number(this.filterWorkflowId) }),
      ...(this.filterRecordSearch.trim() !== '' && { recordSearch: this.filterRecordSearch.trim() })
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        this.items = items;
        // Los ids marcados que ya no aparecen (procesados, cancelados o fuera del filtro)
        // dejarían una selección fantasma que el usuario no puede ver ni desmarcar.
        const vigentes = new Set(items.map(i => i.queueItemId));
        this.selectedIds = new Set([...this.selectedIds].filter(id => vigentes.has(id)));
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la cola de esperas.';
        this.loading = false;
      }
    });
  }

  onFilterChange(): void { this.loadQueue(); }

  /** ¿Hay algún filtro puesto? Distingue "cola vacía" de "filtros que no devuelven nada". */
  get hasActiveFilters(): boolean {
    return !!this.filterStatus || !!this.filterQueueType
        || this.filterWorkflowId !== '' || this.filterRecordSearch.trim() !== '';
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterQueueType = '';
    this.filterWorkflowId = '';
    this.filterRecordSearch = '';
    this.loadQueue();
  }

  processDue(): void {
    this.processing = true;
    this.error = '';
    this.waitApi.processDue(this.includeActive).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const items = Array.isArray(res) ? res : [];
        this.processing = false;
        this.loadQueue();
        // INC_CRM_020 (2) inc3, problema 3 — antes el aviso decía "procesado correctamente"
        // sin decir cuántas ni cuáles, y como la tabla seguía vacía no había forma de
        // comprobar que algo hubiera pasado. Ahora se informa el número y las inscripciones.
        if (items.length === 0) {
          this.toast.info({
            title: 'Nada que procesar',
            message: this.includeActive
              ? 'No había esperas pendientes en la cola.'
              : 'No había esperas vencidas. Marca "incluir activas" para forzar las que aún no vencen.'
          });
          return;
        }
        const inscripciones = [...new Set(items.map(i => i.enrollmentId).filter(id => id != null))];
        const detalle = inscripciones.length > 0
          ? ` Inscripciones: ${inscripciones.slice(0, 5).join(', ')}${inscripciones.length > 5 ? '…' : ''}.`
          : '';
        const alcance = this.includeActive ? 'esperas procesadas (incluyendo activas)' : 'esperas vencidas procesadas';
        this.toast.success({
          title: 'Procesamiento completado',
          message: `${items.length} ${alcance}.${detalle}`
        });
      },
      error: (err) => {
        this.error = err.status === 403
          ? 'No tienes permisos para realizar esta acción.'
          : (err.error?.message || 'Error al procesar esperas.');
        this.processing = false;
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  // ── Selección (INC_CRM_020 (4) inc3) ──────────────────────────────────────
  //
  // "Procesar vencidas" era la única acción disponible y es todo-o-nada: no había forma de
  // ejecutar una espera concreta ni de comprobar sobre cuáles había actuado. Con la selección
  // explícita el usuario marca filas y el aviso posterior enumera exactamente lo procesado.

  /** Ids marcados. Se limpia en cada recarga: los ids ya procesados dejan de existir. */
  selectedIds = new Set<number>();

  isSelected(item: WorkflowWaitQueueResponse): boolean {
    return this.selectedIds.has(item.queueItemId);
  }

  toggleSelected(item: WorkflowWaitQueueResponse): void {
    if (this.selectedIds.has(item.queueItemId)) {
      this.selectedIds.delete(item.queueItemId);
    } else {
      this.selectedIds.add(item.queueItemId);
    }
  }

  /** Solo tiene sentido reanudar lo que sigue pendiente; lo ya procesado no es seleccionable. */
  get selectableItems(): WorkflowWaitQueueResponse[] {
    return this.items.filter(i => i.status === 'PENDING');
  }

  get allSelected(): boolean {
    const seleccionables = this.selectableItems;
    return seleccionables.length > 0 && seleccionables.every(i => this.selectedIds.has(i.queueItemId));
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedIds.clear();
      return;
    }
    this.selectedIds = new Set(this.selectableItems.map(i => i.queueItemId));
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  processSelected(): void {
    const ids = [...this.selectedIds];
    if (!ids.length) return;

    this.processing = true;
    this.error = '';
    this.waitApi.processSelected(ids).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const procesadas = Array.isArray(res) ? res : [];
        this.processing = false;
        this.selectedIds.clear();
        this.loadQueue();
        const inscripciones = [...new Set(procesadas.map(i => i.enrollmentId).filter(id => id != null))];
        const detalle = inscripciones.length > 0
          ? ` Inscripciones: ${inscripciones.slice(0, 5).join(', ')}${inscripciones.length > 5 ? '…' : ''}.`
          : '';
        const omitidas = ids.length - procesadas.length;
        const aviso = omitidas > 0
          ? ` ${omitidas} se omitieron (ya no estaban pendientes).`
          : '';
        this.toast.success({
          title: 'Procesamiento completado',
          message: `${procesadas.length} de ${ids.length} esperas procesadas.${detalle}${aviso}`
        });
      },
      error: (err) => {
        this.error = err.status === 403
          ? 'No tienes permisos para realizar esta acción.'
          : (err.error?.message || 'Error al procesar las esperas seleccionadas.');
        this.processing = false;
        this.toast.error({ title: 'Error', message: this.error });
      }
    });
  }

  openCancelModal(item: WorkflowWaitQueueResponse): void {
    this.cancelItemId = item.queueItemId;
    this.cancelReason = '';
    this.showCancelModal = true;
  }

  confirmCancel(): void {
    if (!this.cancelItemId) return;
    this.cancellingId = this.cancelItemId;
    this.waitApi.cancel(this.cancelItemId, { reason: this.cancelReason || undefined })
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          const id = this.cancelItemId;
          this.closeCancelModal();
          this.loadQueue();
          this.toast.success({ title: 'Espera cancelada', message: `La espera #${id} ya no se ejecutará.` });
        },
        error: (err) => {
          this.error = err.error?.message || 'No se pudo cancelar el item.';
          this.closeCancelModal();
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
    this.cancelItemId = null;
    this.cancelReason = '';
    this.cancellingId = null;
  }

  getStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'PENDING':    return 'bg-yellow-100 text-yellow-700';
      case 'PROCESSING': return 'bg-blue-100 text-blue-700';
      // El motor escribe PROCESSED, nunca COMPLETED: con el valor viejo la fila procesada
      // salía en gris de "estado desconocido" en vez de en verde.
      case 'PROCESSED':  return 'bg-green-100 text-green-700';
      case 'FAILED':     return 'bg-red-100 text-red-700';
      case 'CANCELLED':  return 'bg-gray-100 text-gray-500';
      case 'IGNORED':    return 'bg-gray-100 text-gray-500';
      default:           return 'bg-gray-100 text-gray-500';
    }
  }

  get pendingCount(): number {
    return this.items.filter(i => i.status === 'PENDING').length;
  }

  get dueNow(): number {
    const now = new Date();
    return this.items.filter(i => i.status === 'PENDING' && new Date(i.availableAt) <= now).length;
  }

  isPastDue(item: WorkflowWaitQueueResponse): boolean {
    return item.status === 'PENDING' && new Date(item.availableAt) <= new Date();
  }

  /** Prefiere el campo del backend; si no viene, calcula con availableAt. */
  isOverdue(item: WorkflowWaitQueueResponse): boolean {
    return item.overdue !== undefined ? item.overdue : this.isPastDue(item);
  }

  get overdueCount(): number {
    return this.items.filter(i => this.isOverdue(i)).length;
  }

  get activePendingCount(): number {
    return this.items.filter(i => i.status === 'PENDING' && !this.isOverdue(i)).length;
  }
}
